// src/components/common/InlineAiSuggestions.js
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, X, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Modern Inline AI Suggestions Component
const InlineAiSuggestions = ({
  isVisible,
  onClose,
  suggestions,
  isLoading,
  error,
  onAddSelected,
  selectedSuggestions,
  onToggleSelection,
  onGetMore
}) => {
  const { t } = useTranslation();

  const handleAddClick = () => {
    onAddSelected(selectedSuggestions);
    onClose(); // Close form after adding
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0, height: 0, y: -20 }}
      animate={{ opacity: 1, height: 'auto', y: 0 }}
      exit={{ opacity: 0, height: 0, y: -20 }}
      transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
      className="mb-6 overflow-hidden"
      onKeyDown={handleKeyPress}
      tabIndex={-1}
    >
      <div className="relative bg-gradient-to-br from-purple-500 via-indigo-500 to-pink-500 p-[1px] rounded-2xl shadow-2xl">
        <div className="bg-white rounded-2xl backdrop-blur-sm">
          {/* Animated Header */}
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="flex items-center justify-between p-5 border-b border-slate-100"
          >
            <div className="flex items-center">
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                  <motion.div
                    animate={{
                      rotate: 360,
                      scale: [1, 1.1, 1]
                    }}
                    transition={{
                      rotate: { duration: 3, repeat: Infinity, ease: "linear" },
                      scale: { duration: 2, repeat: Infinity, ease: "easeInOut" }
                    }}
                    className="w-6 h-6"
                  >
                    <Zap className="h-6 w-6 text-yellow-300" />
                  </motion.div>
                </div>
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-pink-500 to-red-500 rounded-full animate-pulse flex items-center justify-center">
                  <span className="text-white text-xs font-bold">AI</span>
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                  🤖 Suggestions IA de Sources de Risque
                </h3>
                <p className="text-xs text-slate-500 font-medium">
                  Suggestions personnalisées pour votre analyse
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-all duration-200 hover:scale-110"
            >
              <X size={20} />
            </button>
          </motion.div>

          {/* Content Area */}
          <div className="p-5 max-h-96 overflow-y-auto">
            {/* Loading State */}
            {isLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-8"
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-12 h-12 mx-auto mb-4"
                >
                  <RefreshCw size={48} className="text-purple-500" />
                </motion.div>
                <p className="text-slate-600 font-medium">🔍 Génération des suggestions IA en cours...</p>
                <div className="mt-2 flex justify-center space-x-1">
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                      className="w-2 h-2 bg-purple-500 rounded-full"
                    />
                  ))}
                </div>
              </motion.div>
            )}

            {/* Error State */}
            {error && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 text-red-700 p-4 rounded-xl mb-4"
              >
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center mr-3">
                    <X size={16} className="text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold">❌ Erreur</h4>
                    <p className="text-sm">{error}</p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Suggestions List */}
            {!isLoading && suggestions && suggestions.length > 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="space-y-3"
              >
                <p className="text-sm text-slate-600 mb-4">
                  Sélectionnez les suggestions que vous souhaitez ajouter à votre analyse.
                  {selectedSuggestions && selectedSuggestions.length > 0 && (
                    <span className="ml-2 font-medium text-purple-600">
                      {selectedSuggestions.length} sélectionnée(s)
                    </span>
                  )}
                </p>
                {suggestions.map((suggestion, index) => (
                  <motion.div
                    key={`${suggestion.name}-${index}`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`border-2 rounded-xl p-4 transition-all duration-300 cursor-pointer hover:scale-[1.02] ${
                      selectedSuggestions && selectedSuggestions.some(s => s.name === suggestion.name)
                        ? 'bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-300 shadow-lg'
                        : 'bg-white hover:bg-slate-50 border-slate-200 hover:border-purple-200'
                    }`}
                    onClick={() => onToggleSelection && onToggleSelection(suggestion)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="relative">
                        <input
                          type="checkbox"
                          className="h-5 w-5 mt-1 text-purple-600 focus:ring-purple-500 border-slate-300 rounded-md"
                          checked={selectedSuggestions && selectedSuggestions.some(s => s.name === suggestion.name)}
                          onChange={() => onToggleSelection && onToggleSelection(suggestion)}
                          id={`ai-suggestion-${index}`}
                        />
                        {selectedSuggestions && selectedSuggestions.some(s => s.name === suggestion.name) && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full flex items-center justify-center"
                          >
                            <span className="text-white text-xs">✓</span>
                          </motion.div>
                        )}
                      </div>
                      <div className="flex-1">
                        <label htmlFor={`ai-suggestion-${index}`} className="font-semibold text-slate-800 cursor-pointer block hover:text-purple-600 transition-colors">
                          💡 {suggestion.name}
                        </label>
                        {suggestion.description && (
                          <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                            {suggestion.description}
                          </p>
                        )}
                        <div className="mt-2 flex flex-wrap gap-2">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${
                            suggestion.objectifVise === 'confidentialite' ? 'bg-blue-100 text-blue-800' :
                            suggestion.objectifVise === 'integrite' ? 'bg-green-100 text-green-800' :
                            suggestion.objectifVise === 'disponibilite' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-purple-100 text-purple-800'
                          }`}>
                            {suggestion.objectifVise || 'Confidentialité'}
                          </span>
                          <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-800">
                            {suggestion.category || 'Non catégorisé'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              !isLoading && suggestions && suggestions.length === 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-10"
                >
                  <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 rounded-full flex items-center justify-center">
                    <span className="text-2xl">🤷‍♂️</span>
                  </div>
                  <p className="text-slate-500 font-medium">Aucune suggestion disponible</p>
                </motion.div>
              )
            )}
          </div>

          {/* Action Footer */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="flex justify-between items-center p-5 border-t border-slate-100 bg-gradient-to-r from-slate-50 to-blue-50 rounded-b-2xl"
          >
            {/* Left side: Selection count */}
            <span className="text-sm font-medium text-slate-600 bg-white px-3 py-1 rounded-full border">
              {selectedSuggestions ? selectedSuggestions.length : 0} sélectionnée{selectedSuggestions && selectedSuggestions.length !== 1 ? 's' : ''}
            </span>

            {/* Right side: Actions */}
            <div className="flex items-center space-x-3">
              {onGetMore && (
                <button
                  onClick={onGetMore}
                  disabled={isLoading}
                  className="px-4 py-2.5 text-sm font-semibold text-purple-600 bg-purple-100 hover:bg-purple-200 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  🔄 Plus
                </button>
              )}
              <button
                onClick={onClose}
                className="px-4 py-2.5 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95"
              >
                ❌ Annuler
              </button>
              <button
                onClick={handleAddClick}
                disabled={!selectedSuggestions || selectedSuggestions.length === 0 || isLoading}
                className={`px-6 py-2.5 text-sm font-semibold text-white rounded-xl transition-all duration-200 hover:scale-105 active:scale-95 shadow-lg ${
                  selectedSuggestions && selectedSuggestions.length > 0 && !isLoading
                    ? 'bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 shadow-purple-500/25'
                    : 'bg-slate-400 cursor-not-allowed'
                }`}
              >
                {selectedSuggestions && selectedSuggestions.length > 0 ? '🚀 Ajouter' : '⏳ Ajouter'}
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default InlineAiSuggestions;
